// Aignoz Innovation Matrix — /matrix (Phase 3)
// 3-axis (Biology × Technology × Healthcare Function) idea explorer.
// Generates one 5-Layer concept card per LLM call (or deterministic via dry-run).
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Atom, Sparkles, Loader2, BookmarkPlus, Rocket, X, Dna, Cpu, Brain, Boxes,
  TrendingUp, ChevronRight, AlertTriangle, Beaker, RefreshCw, Lock, Unlock, Dice5,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API } from "@/lib";

const MAGENTA = "#D17EC0";

// Mirror /app/backend/constants.py (single source of truth on the backend).
const BIOLOGY_AXIS = [
  "Immune System", "Nervous System", "Cardiovascular System", "Respiratory System",
  "Reproductive System", "Endocrine System", "Musculoskeletal System",
  "Microbiome", "Aging Biology", "Developmental Biology",
];
const TECHNOLOGY_AXIS = [
  "AI", "Agentic AI", "Digital Twins", "Genomics", "Transcriptomics", "Proteomics",
  "Glycomics", "CRISPR", "Synthetic Biology", "Microfluidics", "Nanotechnology",
  "Robotics", "Spatial Biology", "Quantum Computing", "Advanced Sensors",
];
const FUNCTION_AXIS = [
  "Prevention", "Screening", "Diagnosis", "Risk Prediction", "Monitoring",
  "Treatment", "Drug Discovery", "Rehabilitation", "Public Health", "Precision Medicine",
];

const LAYER_DEFS = [
  { id: "biology",      label: "Biology",      n: 1, icon: Dna,        color: "#34D399", border: "rgba(52,211,153,0.45)",  bg: "rgba(52,211,153,0.06)" },
  { id: "technology",   label: "Technology",   n: 2, icon: Cpu,        color: "#22D3EE", border: "rgba(34,211,238,0.45)",  bg: "rgba(34,211,238,0.06)" },
  { id: "intelligence", label: "Intelligence", n: 3, icon: Brain,      color: "#A78BFA", border: "rgba(167,139,250,0.45)", bg: "rgba(167,139,250,0.06)" },
  { id: "platform",     label: "Platform",     n: 4, icon: Boxes,      color: "#FBBF24", border: "rgba(251,191,36,0.45)",  bg: "rgba(251,191,36,0.06)" },
  { id: "business",     label: "Business",     n: 5, icon: TrendingUp, color: MAGENTA,   border: "rgba(209,126,192,0.55)", bg: "rgba(209,126,192,0.08)" },
];

const LS_PREFILL_KEY = "aignoz.matrix.prefill";

// ===========================================================================
// PAGE
// ===========================================================================
export default function MatrixPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [biology, setBiology] = useState(null);
  const [technology, setTechnology] = useState(null);
  const [func, setFunc] = useState(null);
  const [locks, setLocks] = useState({ biology: false, technology: false, function: false });
  const [concept, setConcept] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rolling, setRolling] = useState(false);
  const [rollRationale, setRollRationale] = useState(null);
  const [recent, setRecent] = useState([]);
  const [prefillFromProblem, setPrefillFromProblem] = useState(null);

  // ---------- Prefill from /sources ----------
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_PREFILL_KEY);
      if (raw) {
        const j = JSON.parse(raw);
        setPrefillFromProblem(j);
        localStorage.removeItem(LS_PREFILL_KEY);
        toast("Loaded context from /sources", { description: j.text?.slice(0, 90) });
      }
    } catch { /* ignore */ }
  }, [location.pathname]);

  // ---------- Recent concepts ----------
  async function refreshRecent() {
    try {
      const r = await fetch(`${API}/matrix/concepts?limit=5`);
      const j = await r.json();
      setRecent(j.concepts || []);
    } catch { /* silent */ }
  }
  useEffect(() => { refreshRecent(); }, []);

  const allSelected = biology && technology && func;
  const nLocked = (locks.biology ? 1 : 0) + (locks.technology ? 1 : 0) + (locks.function ? 1 : 0);
  const canRoll = nLocked >= 1 && nLocked <= 2;

  function toggleLock(axis, val) {
    setLocks((cur) => {
      const next = { ...cur };
      // If trying to lock and we're already at 2 locks, do nothing.
      if (!next[axis] && nLocked >= 2) {
        toast("Max 2 locks", { description: "Unlock another chip first." });
        return cur;
      }
      next[axis] = !next[axis];
      // Auto-pick the chip if locking without a selection.
      if (next[axis] && !val) return cur;
      return next;
    });
  }

  function lockOrSelect(axis, value, setter) {
    setter(value);
    // Don't auto-lock — locking is an explicit action via the corner icon.
  }

  async function roll() {
    if (!canRoll) return;
    setRolling(true);
    setRollRationale(null);
    try {
      const body = {
        biology: locks.biology ? biology : null,
        technology: locks.technology ? technology : null,
        function: locks.function ? func : null,
      };
      const r = await fetch(`${API}/matrix/roll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      // Animate to the suggested values for any axis that wasn't locked.
      if (!locks.biology)    setBiology(j.biology);
      if (!locks.technology) setTechnology(j.technology);
      if (!locks.function)   setFunc(j.function);
      setRollRationale(j.rationale);
      toast.success("Rolled", { description: j.rationale.slice(0, 70) });
    } catch (e) {
      toast.error("Roll failed", { description: String(e.message || e) });
    } finally {
      setRolling(false);
    }
  }

  async function generate() {
    if (!allSelected) return;
    setBusy(true);
    setConcept(null);
    try {
      const r = await fetch(`${API}/matrix/concept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ biology, technology, function: func }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const c = await r.json();
      setConcept(c);
      toast.success("Concept generated", { description: c.concept_name });
    } catch (e) {
      toast.error("Generation failed", { description: String(e.message || e) });
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setBiology(null); setTechnology(null); setFunc(null);
    setConcept(null); setLocks({ biology: false, technology: false, function: false });
    setRollRationale(null);
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="matrix-page">
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        {/* ============================ HEADER ============================ */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.18em] font-medium"
              style={{ background: "rgba(209,126,192,0.10)", borderColor: "rgba(209,126,192,0.45)", color: "#F0B6DD" }}
            >
              <Atom className="w-3 h-3" /> Innovation Matrix™
            </span>
            <span className="text-[10px] text-white/40 mono uppercase tracking-[0.16em]">Phase 3 · 1,500 cells</span>
          </div>
          <h1 className="text-4xl font-semibold tracking-tight leading-tight">
            Pick a biology, a technology, a function. We invent the venture.
          </h1>
          <p className="text-base text-white/55 italic mt-2 max-w-2xl leading-snug">
            Every Aignoz concept lives at one cell of a 10 × 15 × 10 cube. Select one chip per column —
            we&apos;ll output a 5-layer card with a venture name, formula, and a calibrated quick-score.
          </p>

          {/* Live formula */}
          <div className="mt-5 rounded-xl border border-white/[0.08] bg-white/[0.012] px-5 py-4 flex flex-wrap items-center gap-3"
               data-testid="matrix-formula">
            <FormulaChip label="Biology"   value={biology}   placeholder="{select}" hue="emerald" />
            <Times />
            <FormulaChip label="Technology" value={technology} placeholder="{select}" hue="cyan" />
            <Times />
            <FormulaChip label="Function"  value={func}      placeholder="{select}" hue="violet" />
            <span className="text-white/50 mono">=</span>
            <span className="mono text-base font-semibold" style={{ color: MAGENTA }}>
              {concept?.concept_name || "?"}
            </span>
            <div className="ml-auto flex items-center gap-2">
              {(biology || technology || func) && (
                <Button variant="ghost" size="sm" onClick={reset}
                  className="text-[10px] uppercase tracking-[0.14em] text-white/55 hover:text-white"
                  data-testid="matrix-reset-btn">
                  <X className="w-3 h-3 mr-1" /> Reset
                </Button>
              )}
              <Button
                onClick={roll}
                disabled={!canRoll || rolling}
                variant="outline"
                size="sm"
                data-testid="matrix-roll-btn"
                className="gap-1.5 disabled:opacity-40"
              >
                {rolling
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Dice5 className="w-3 h-3" />}
                Roll
              </Button>
              <Button
                onClick={generate}
                disabled={!allSelected || busy}
                data-testid="matrix-generate-btn"
                className="bg-teal-500 hover:bg-teal-400 text-black font-medium gap-2 disabled:opacity-40"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate Concept
              </Button>
            </div>
          </div>

          {prefillFromProblem && (
            <div className="mt-3 rounded-md border border-violet-400/30 bg-violet-500/[0.05] px-4 py-2.5 text-xs text-white/80 flex items-start gap-2"
                 data-testid="matrix-prefill-banner">
              <Beaker className="w-3.5 h-3.5 text-violet-300 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.14em] text-violet-200/80 mb-0.5">
                  Context from /sources · {prefillFromProblem.source}
                </div>
                <div className="leading-snug">{prefillFromProblem.text}</div>
                {(prefillFromProblem.biology_hint || prefillFromProblem.tech_hint) && (
                  <div className="text-[10px] text-white/55 mt-1">
                    {prefillFromProblem.biology_hint && <span>Biology hint: {prefillFromProblem.biology_hint}</span>}
                    {prefillFromProblem.biology_hint && prefillFromProblem.tech_hint && <span className="mx-1.5">·</span>}
                    {prefillFromProblem.tech_hint && <span>Tech hint: {prefillFromProblem.tech_hint}</span>}
                  </div>
                )}
              </div>
              <button onClick={() => setPrefillFromProblem(null)} className="text-white/40 hover:text-white/80">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {rollRationale ? (
            <div className="mt-3 rounded-md border border-amber-400/30 bg-amber-500/[0.05] px-4 py-2.5 text-xs text-white/85 flex items-start gap-2"
                 data-testid="matrix-roll-rationale">
              <Dice5 className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
              <div className="flex-1 leading-snug">
                <span className="text-[10px] uppercase tracking-[0.14em] text-amber-200/80 mr-1.5">Roll:</span>
                {rollRationale}
              </div>
              <button onClick={() => setRollRationale(null)} className="text-white/40 hover:text-white/80">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            // Always-mounted empty node so external assertions can find the testid before any roll.
            <div data-testid="matrix-roll-rationale" className="hidden" aria-hidden="true" />
          )}
        </header>

        {/* ============================ MAIN GRID ============================ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_360px] gap-5">
          {/* Axis 1 — Biology (10) */}
          <AxisColumn
            testId="matrix-axis-biology"
            label="Biology"
            sublabel="10 systems"
            count={1}
            total={10}
            items={BIOLOGY_AXIS}
            selected={biology}
            onPick={setBiology}
            chipHue="emerald"
            chipTestidPrefix="matrix-chip-bio"
            axisKey="biology"
            locked={locks.biology}
            onToggleLock={() => toggleLock("biology", biology)}
          />
          {/* Axis 2 — Technology (15) */}
          <AxisColumn
            testId="matrix-axis-technology"
            label="Technology"
            sublabel="15 domains"
            count={2}
            total={15}
            items={TECHNOLOGY_AXIS}
            selected={technology}
            onPick={setTechnology}
            chipHue="cyan"
            chipTestidPrefix="matrix-chip-tech"
            axisKey="technology"
            locked={locks.technology}
            onToggleLock={() => toggleLock("technology", technology)}
          />
          {/* Axis 3 — Function (10) */}
          <AxisColumn
            testId="matrix-axis-function"
            label="Healthcare Function"
            sublabel="10 functions"
            count={3}
            total={10}
            items={FUNCTION_AXIS}
            selected={func}
            onPick={setFunc}
            chipHue="violet"
            chipTestidPrefix="matrix-chip-fn"
            axisKey="function"
            locked={locks.function}
            onToggleLock={() => toggleLock("function", func)}
          />

          {/* Right rail */}
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {concept ? (
                <motion.div
                  key="card"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 12 }}
                  transition={{ duration: 0.35 }}
                >
                  <ConceptCard
                    concept={concept}
                    onSaved={(saved) => {
                      refreshRecent();
                      toast.success("Saved to Idea Vault", { description: saved.concept_name });
                    }}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="rounded-xl border border-white/[0.06] bg-white/[0.012] p-6 text-center"
                  data-testid="matrix-empty-card"
                >
                  <Atom className="w-7 h-7 mx-auto mb-2" style={{ color: MAGENTA, opacity: 0.7 }} />
                  <div className="text-sm font-semibold text-white/85">Pick 3 chips to begin</div>
                  <div className="text-xs text-white/45 mt-1 leading-relaxed">
                    Selected chips light up. Once all three columns have a selection, the
                    <span className="text-teal-200"> Generate Concept </span>
                    button activates and we&apos;ll synthesise a 5-Layer card here.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <RecentConcepts items={recent} onRefresh={refreshRecent} />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-[10px] text-white/30 leading-relaxed">
          Designed by <span className="text-white/55">Dr. Sriinidhi BV (MBBS, MD)</span> · Aignoz Innovation OS™
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// AXIS COLUMN
// ===========================================================================
function AxisColumn({ testId, label, sublabel, count, total, items, selected, onPick, chipHue, chipTestidPrefix, axisKey, locked, onToggleLock }) {
  const hueRing = {
    emerald: "ring-emerald-400/40 bg-emerald-400/15 border-emerald-400/45 text-emerald-100",
    cyan:    "ring-cyan-400/40 bg-cyan-400/15 border-cyan-400/45 text-cyan-100",
    violet:  "ring-violet-400/40 bg-violet-400/15 border-violet-400/45 text-violet-100",
  }[chipHue];
  const lockedGlow = {
    emerald: "shadow-[0_0_0_2px_rgba(52,211,153,0.30)]",
    cyan:    "shadow-[0_0_0_2px_rgba(34,211,238,0.30)]",
    violet:  "shadow-[0_0_0_2px_rgba(167,139,250,0.30)]",
  }[chipHue];
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.012] p-4" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[9px] mono text-teal-300/70 mb-0.5">AXIS {count} / 3</div>
          <div className="text-sm font-semibold tracking-tight text-white/90">{label}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] mono text-white/35">{sublabel}</span>
          {selected && (
            <button
              onClick={onToggleLock}
              data-testid={`matrix-chip-lock-${axisKey}`}
              title={locked ? "Unlock axis" : "Lock axis"}
              className={`p-1 rounded border text-white/55 hover:text-white transition ${
                locked
                  ? "border-amber-400/50 bg-amber-500/10 text-amber-200"
                  : "border-white/[0.10] hover:border-white/[0.20]"
              }`}
            >
              {locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>
      <div className={`flex flex-wrap gap-1.5 ${locked ? "rounded-md " + lockedGlow + " p-1 -m-1" : ""}`}>
        {items.map((it) => {
          const isSel = it === selected;
          const slug = it.toLowerCase().replace(/\s+/g, "-");
          return (
            <button
              key={it}
              data-testid={`${chipTestidPrefix}-${slug}`}
              onClick={() => !locked && onPick(it)}
              disabled={locked && !isSel}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                isSel
                  ? `ring-1 ${hueRing}`
                  : "border-white/[0.08] text-white/65 hover:border-white/[0.18] hover:text-white"
              } ${locked && !isSel ? "opacity-30 cursor-not-allowed" : ""}`}
            >
              {it}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// CONCEPT CARD (5-layer) + actions
// ===========================================================================
function ConceptCard({ concept, onSaved }) {
  const navigate = useNavigate();
  const [savedId, setSavedId] = useState(null);
  const [savingBusy, setSavingBusy] = useState(false);
  const [promoteBusy, setPromoteBusy] = useState(false);
  const score = concept.quick_score ?? 0;
  const scoreColor = score >= 80 ? "#5EEAD4" : score >= 65 ? "#FBBF24" : "#F87171";

  async function save() {
    setSavingBusy(true);
    try {
      const r = await fetch(`${API}/matrix/concepts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(concept),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setSavedId(j.id);
      onSaved?.({ ...concept, id: j.id, created_at: j.created_at });
    } catch (e) {
      toast.error("Save failed", { description: String(e.message || e) });
    } finally { setSavingBusy(false); }
  }

  async function promote() {
    setPromoteBusy(true);
    try {
      const body = {
        problem: `${concept.aignoz_formula}. ${concept.layers?.biology || ""}`.trim(),
        population: "global",
        constraints: `Promoted from Innovation Matrix concept "${concept.concept_name}".`,
        geography: "Global",
      };
      const r = await fetch(`${API}/innovate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      toast.success("Promoted to 12-agent venture", { description: "Opening workspace…" });
      setTimeout(() => navigate(`/projects/${j.run_id}/overview`), 600);
    } catch (e) {
      toast.error("Promotion failed", { description: String(e.message || e) });
    } finally { setPromoteBusy(false); }
  }

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.012] overflow-hidden"
         data-testid="matrix-concept-card">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/[0.06]"
           style={{ background: "linear-gradient(135deg, rgba(209,126,192,0.08), rgba(94,234,212,0.02))" }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80 mb-1">Generated Concept</div>
            <div className="text-2xl font-semibold tracking-tight" style={{ color: MAGENTA }}>
              {concept.concept_name}
            </div>
            <div className="text-[11px] text-white/55 mono mt-1 leading-snug">
              {concept.aignoz_formula}
            </div>
          </div>
          <div className="shrink-0 text-right" data-testid="matrix-quick-score">
            <div className="text-[9px] uppercase tracking-[0.14em] text-white/45 mb-0.5">Quick score</div>
            <div className="mono text-2xl font-semibold" style={{ color: scoreColor }}>
              {score}<span className="text-white/30 text-sm">/100</span>
            </div>
          </div>
        </div>
      </div>

      {/* 5 layers */}
      <div className="p-4 space-y-2">
        {LAYER_DEFS.map((L) => (
          <div key={L.id}
               data-testid={`matrix-layer-${L.id}`}
               className="rounded-md border px-3 py-2.5 flex items-start gap-3"
               style={{ borderColor: L.border, background: L.bg }}>
            <div className="shrink-0 w-7 h-7 rounded border flex items-center justify-center"
                 style={{ borderColor: L.border, background: "rgba(0,0,0,0.2)" }}>
              <L.icon className="w-3.5 h-3.5" style={{ color: L.color }} />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] mono uppercase tracking-[0.14em]" style={{ color: L.color }}>
                L{L.n} · {L.label}
              </div>
              <div className="text-[12px] text-white/85 leading-snug mt-0.5">
                {concept.layers?.[L.id] || "—"}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-white/[0.06] flex items-center gap-2 bg-white/[0.01]">
        <Button
          onClick={save}
          disabled={savingBusy || !!savedId}
          variant="outline"
          size="sm"
          data-testid="matrix-save-btn"
          className="flex-1 gap-1.5"
        >
          {savingBusy
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <BookmarkPlus className="w-3 h-3" />}
          {savedId ? "Saved to Vault" : "Save to Idea Vault"}
        </Button>
        <Button
          onClick={promote}
          disabled={promoteBusy}
          size="sm"
          data-testid="matrix-promote-btn"
          className="flex-1 gap-1.5 bg-teal-500 hover:bg-teal-400 text-black font-medium"
        >
          {promoteBusy
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <Rocket className="w-3 h-3" />}
          Promote to Full Venture
        </Button>
      </div>
    </div>
  );
}

// ===========================================================================
// RECENT CONCEPTS RAIL
// ===========================================================================
function RecentConcepts({ items, onRefresh }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.012] p-4" data-testid="matrix-recent-rail">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">Idea Vault · last 5</div>
        <button onClick={onRefresh} className="text-white/40 hover:text-white/80" data-testid="matrix-recent-refresh">
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-white/40 italic text-center py-5">
          No saved concepts yet. Generate one, then click <em>Save to Idea Vault</em>.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((c) => (
            <li key={c.id || c.created_at}
                data-testid={`matrix-recent-${(c.id || "").slice(0, 8)}`}
                className="rounded-md border border-white/[0.06] bg-white/[0.015] px-2.5 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[12px] font-semibold tracking-tight truncate" style={{ color: MAGENTA }}>
                  {c.concept_name}
                </div>
                <span className="text-[9px] mono text-teal-200/80 shrink-0">{c.quick_score ?? "—"}</span>
              </div>
              <div className="text-[10px] text-white/45 mono truncate mt-0.5">{c.aignoz_formula}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ===========================================================================
// FORMULA HEADER BITS
// ===========================================================================
function FormulaChip({ label, value, placeholder, hue }) {
  const dim = !value;
  const hueDot = { emerald: "bg-emerald-400", cyan: "bg-cyan-400", violet: "bg-violet-400" }[hue];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[12px] ${
      dim
        ? "border-white/[0.08] bg-white/[0.012] text-white/40 italic"
        : "border-white/[0.15] bg-white/[0.04] text-white/95"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${hueDot}`} />
      <span className="text-[9px] uppercase tracking-[0.14em] text-white/40">{label}</span>
      <span className="font-medium">{value || placeholder}</span>
    </span>
  );
}

function Times() {
  return <span className="text-white/35 mono">×</span>;
}
